/** @jsxImportSource preact */
import { useState } from "preact/hooks";

import * as copy from "../../paraglide/messages.js";
import { FormButton } from "../form/form-button";
import { AlertIcon, InfoIcon, RefreshIcon, SpinnerIcon } from "../icons";
import {
  PanelSheetClose,
  PanelSheetLayer,
  type PanelSheetLayerProps,
} from "../sheet/panel-sheet";
import type { WidgetTokens } from "../theme";
import { CART_SHEET_STYLES } from "./cart-styles";
import { QuantityStepper, type QuantityStepperProps } from "./quantity-stepper";

/**
 * 加购和改数量用的底部面板：聊天窗通用的底部面板，加上加购面板自己的样式。
 */
export function CartSheetLayer({ children, ...props }: PanelSheetLayerProps) {
  return (
    <PanelSheetLayer {...props}>
      <style>{CART_SHEET_STYLES}</style>
      {children}
    </PanelSheetLayer>
  );
}

export interface CartStatus {
  tone: "plain" | "info" | "error";
  text: string;
}

/** 底部固定两行高的状态行：一次只说一件事，换文字时面板高度不变。 */
export function CartStatusLine({ status }: { status: CartStatus }) {
  return (
    <div class="gn-cart-status" data-tone={status.tone} role="status">
      {status.tone === "info" && <InfoIcon size={15} />}
      {status.tone === "error" && <AlertIcon size={15} />}
      <span title={status.text}>{status.text}</span>
    </div>
  );
}

export interface CartPrimaryAction {
  label: string;
  disabled: boolean;
  /** 写入中：按钮里转圈，读屏也知道在忙。 */
  busy: boolean;
  /** 失败后的重试：按钮前摆一个重试图标。 */
  retry: boolean;
  onClick: () => void;
}

function PrimaryButton({
  tokens,
  action,
}: {
  tokens: WidgetTokens;
  action: CartPrimaryAction;
}) {
  return (
    <FormButton
      disabled={action.disabled}
      fullWidth
      onClick={action.onClick}
      tokens={tokens}
    >
      {action.busy && (
        <span class="gn-cart-spin">
          <SpinnerIcon size={16} />
        </span>
      )}
      {action.retry && !action.busy && <RefreshIcon size={16} />}
      <span>{action.label}</span>
    </FormButton>
  );
}

function Thumb({
  imageUrl,
  small = false,
}: {
  imageUrl?: string;
  small?: boolean;
}) {
  // 图取不回来就只留底色方块，不在买家眼前摆破图标。
  const [failed, setFailed] = useState<string>();
  return (
    <div class="gn-cart-thumb" data-small={small || undefined}>
      {imageUrl !== undefined && failed !== imageUrl && (
        <img
          alt=""
          onError={() => {
            setFailed(imageUrl);
          }}
          src={imageUrl}
        />
      )}
    </div>
  );
}

export interface CartValueView {
  value: string;
  pressed: boolean;
  /** 结合其它已选项已经买不到：划掉，但仍可点，点了在状态行说明售罄。 */
  soldOut: boolean;
}

export interface CartOptionView {
  name: string;
  /** 选项名，选了值后跟上当前值：`Color · White`。 */
  heading: string;
  /** 取值都很短（尺码、鞋码）就排成等宽网格。 */
  grid: boolean;
  values: CartValueView[];
}

export interface AddSheetViewProps {
  tokens: WidgetTokens;
  titleId: string;
  title: string;
  price: string;
  imageUrl?: string;
  options: CartOptionView[];
  onPick: (index: number, value: string) => void;
  quantity: Omit<QuantityStepperProps, "label" | "disabled">;
  status: CartStatus;
  primary: CartPrimaryAction;
  /** 结果没法确认时并排给一个查看购物车：先看一眼再决定要不要重试。 */
  cartHref?: string;
  /** 写入中：选项、数量、关闭都不可动。 */
  locked: boolean;
  onClose: () => void;
}

/** 选变体和数量的面板内容：商品、选项、数量、状态行和加购按钮。 */
export function AddSheetView({
  tokens,
  titleId,
  title,
  price,
  imageUrl,
  options,
  onPick,
  quantity,
  status,
  primary,
  cartHref,
  locked,
  onClose,
}: AddSheetViewProps) {
  return (
    <>
      <div class="gn-cart-head">
        <Thumb imageUrl={imageUrl} />
        <div class="gn-cart-heading">
          <h2 class="gn-cart-title" id={titleId}>
            {title}
          </h2>
          <div class="gn-cart-price">{price}</div>
        </div>
        <PanelSheetClose disabled={locked} onClick={onClose} />
      </div>
      {options.map((option, index) => (
        <div
          aria-label={option.name}
          class="gn-cart-option"
          key={option.name}
          role="group"
        >
          <div aria-hidden="true" class="gn-cart-option-name">
            {option.heading}
          </div>
          <div class="gn-cart-values" data-grid={option.grid || undefined}>
            {option.values.map((value) => (
              <button
                aria-label={
                  value.soldOut
                    ? copy.widget_cart_value_sold_out({ value: value.value })
                    : undefined
                }
                aria-pressed={value.pressed}
                class="gn-cart-value"
                data-sold-out={value.soldOut || undefined}
                disabled={locked}
                key={value.value}
                onClick={() => {
                  onPick(index, value.value);
                }}
                type="button"
              >
                {value.value}
              </button>
            ))}
          </div>
        </div>
      ))}
      <QuantityStepper
        {...quantity}
        disabled={locked}
        label={copy.widget_cart_quantity()}
      />
      <div class="gn-cart-foot">
        <CartStatusLine status={status} />
        <div class="gn-cart-actions">
          {cartHref !== undefined && (
            <a class="gn-btn" data-variant="secondary" href={cartHref}>
              {copy.widget_product_cart()}
            </a>
          )}
          <PrimaryButton action={primary} tokens={tokens} />
        </div>
      </div>
    </>
  );
}

export interface EditSheetViewProps {
  tokens: WidgetTokens;
  titleId: string;
  title: string;
  variant?: string;
  imageUrl?: string;
  /** Shopify 确认的这一行现在的件数；还没读到就不写。 */
  inCart?: number;
  quantity: Omit<QuantityStepperProps, "label" | "disabled">;
  status: CartStatus;
  primary: CartPrimaryAction;
  locked: boolean;
  /** 购物车里读不到这一行：数量框也不能改。 */
  frozen: boolean;
  onClose: () => void;
}

/** 改数量的面板内容：哪件商品、现在几件、改成几件。 */
export function EditSheetView({
  tokens,
  titleId,
  title,
  variant,
  imageUrl,
  inCart,
  quantity,
  status,
  primary,
  locked,
  frozen,
  onClose,
}: EditSheetViewProps) {
  return (
    <>
      <div class="gn-cart-head">
        <h2 class="gn-cart-title" id={titleId}>
          {copy.widget_cart_edit_title()}
        </h2>
        <PanelSheetClose disabled={locked} onClick={onClose} />
      </div>
      <div class="gn-cart-head" data-line="">
        <Thumb imageUrl={imageUrl} small />
        <div class="gn-cart-heading">
          <div class="gn-cart-name">{title}</div>
          {variant !== undefined && <div class="gn-cart-meta">{variant}</div>}
        </div>
        {inCart !== undefined && (
          <div class="gn-cart-meta">
            {copy.widget_cart_now_in_cart({ count: inCart })}
          </div>
        )}
      </div>
      <QuantityStepper
        {...quantity}
        disabled={locked || frozen}
        label={copy.widget_cart_new_quantity()}
      />
      <div class="gn-cart-foot">
        <CartStatusLine status={status} />
        <PrimaryButton action={primary} tokens={tokens} />
      </div>
    </>
  );
}
